/**
 * VenueFlow Toast Component
 * 
 * Sliding notification toast for submission feedback.
 */

import React, { useState, useEffect, useCallback } from "react";

// ============================================================
// TOAST STYLES
// ============================================================
const styles = {
  toast: {
    position: "fixed",
    top: 16,
    right: 16,
    transform: "translateY(-120%)",
    background: "#f8fafc",
    color: "#0f172a",
    padding: "14px 18px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
    zIndex: 9999,
    minWidth: 320,
    maxWidth: 420,
    transition: "transform 0.35s ease, opacity 0.35s ease",
    opacity: 0,
  },
  toastVisible: {
    transform: "translateY(0)",
    opacity: 1,
  },
  toastError: {
    borderLeft: "4px solid #ef4444",
  },
  toastSuccess: {
    borderLeft: "4px solid #2fd6b5",
  },
  accent: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    background: "linear-gradient(180deg, #373737, #2fd6b5)",
    borderRadius: "12px 0 0 12px",
  },
  title: {
    display: "block",
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
    color: "#0f172a",
  },
  titleError: {
    color: "#ef4444",
  },
  message: {
    fontSize: 13,
    color: "#475569",
  },
};

// ============================================================
// TOAST COMPONENT
// ============================================================
export function Toast({ show, title, message, isError, duration = 10000, onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => setVisible(true));
      
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onClose?.(), 350);
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [show, duration, onClose]);

  if (!show && !visible) return null;

  return (
    <div
      style={{
        ...styles.toast,
        ...(visible ? styles.toastVisible : {}),
        ...(isError ? styles.toastError : styles.toastSuccess),
      }}
    >
      {!isError && <div style={styles.accent} />}
      <div>
        <strong style={{ ...styles.title, ...(isError ? styles.titleError : {}) }}>
          {title || (isError ? "Submission incomplete" : "Submission received")}
        </strong>
        <div
          style={styles.message}
          dangerouslySetInnerHTML={{ __html: message?.replace(/\n/g, "<br>") || "" }}
        />
      </div>
    </div>
  );
}

// ============================================================
// TOAST HOOK
// ============================================================
export function useToast() {
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    isError: false,
    duration: 10000,
  });

  const showToast = useCallback((message, options = {}) => {
    setToast({
      show: true,
      message,
      title: options.title || "",
      isError: options.isError || false,
      duration: options.duration || 10000,
    });
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, show: false }));
  }, []);

  const showSuccess = useCallback(
    (message, options = {}) => {
      showToast(message, { ...options, isError: false });
    },
    [showToast]
  );

  const showError = useCallback(
    (message, options = {}) => {
      showToast(message, { ...options, isError: true });
    },
    [showToast]
  );

  return {
    toast,
    showToast,
    showSuccess,
    showError,
    hideToast,
    ToastComponent: (
      <Toast
        show={toast.show}
        title={toast.title}
        message={toast.message}
        isError={toast.isError}
        duration={toast.duration}
        onClose={hideToast}
      />
    ),
  };
}

export default Toast;
